type TryParseJsonResult =
  | { isValid: true; value: unknown; error?: undefined }
  | { isValid: false; value?: undefined; error: Error };

export function tryParseJson(json: string): TryParseJsonResult {
  try {
    const value: unknown = json === '' ? undefined : JSON.parse(json);
    return { isValid: true, value };
  } catch (error) {
    return { isValid: false, error: error as Error };
  }
}

/**
 * Parses a JSON string, falling back to the string itself when it is not valid JSON.
 *
 * For payloads that are meant to be JSON but come from outside our control, where showing the
 * raw text is more useful than discarding it.
 *
 * @param value - The JSON string to parse
 * @returns The parsed value, or `value` when it does not parse
 */
export function parseJsonOrFallbackToValue(value: string): unknown {
  const result = tryParseJson(value);
  return result.isValid ? result.value : value;
}

/**
 * Safely stringifies and truncates objects to prevent DoS attacks
 * from extremely large responses that could freeze the browser
 *
 * @param value - The value to stringify
 * @param maxLength - Maximum length before truncation (default: 10000)
 * @returns A safely truncated string representation of the value
 */
export function safeStringify(value: unknown, maxLength: number = 10000): string {
  try {
    const stringified = JSON.stringify(value);

    if (stringified.length <= maxLength) {
      return stringified;
    }

    // Truncate and add indicator
    const truncated = stringified.substring(0, maxLength);
    return `${truncated}... [truncated - result too large (${stringified.length} chars)]`;
  } catch (error) {
    // Fallback if JSON.stringify fails
    return `[Error serializing result: ${error instanceof Error ? error.message : 'Unknown error'}]`;
  }
}
